import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import WorkCard from '@/components/base/WorkCard';
import { usePageMeta } from '@/hooks/usePageMeta';
import { news } from '@/mocks/news';
import { works } from '@/mocks/works';

export default function Detail() {
  const { id } = useParams<{ id: string }>();

  const work = useMemo(() => works.find((w) => w.id === id), [id]);
  const article = useMemo(() => news.find((n) => n.id === id), [id]);

  const relatedWorks = useMemo(() => {
    if (!work) return [];
    return works
      .filter((w) => w.id !== work.id && w.type === work.type && w.genres.some((g) => work.genres.includes(g)))
      .sort((a, b) => a.rank - b.rank)
      .slice(0, 4);
  }, [work]);

  const relatedNews = useMemo(() => {
    if (!article) return [];
    return news.filter((n) => n.id !== article.id).slice(0, 3);
  }, [article]);

  const isMovie = work?.type === 'movie';
  const typeTitle = isMovie ? '电影' : '电视剧';

  usePageMeta(
    work
      ? `${work.title}（${work.year}）详情 - 枫与铃`
      : article
        ? `${article.title} - 枫与铃`
        : '内容不存在 - 枫与铃',
    work ? work.summary.slice(0, 60) : article?.summary.slice(0, 60),
  );

  if (!work && !article) {
    return (
      <div className="mx-auto flex w-full max-w-7xl flex-col items-center px-4 py-28 text-center md:px-6">
        <i className="ri-file-unknow-line text-5xl text-foreground-400" />
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">影视内容不存在或已被删除</h1>
        <p className="mt-3 text-sm text-foreground-600">你访问的内容可能已下线，不如去看看其他精彩推荐。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line" />
          返回首页
        </Link>
      </div>
    );
  }

  if (work) {
    return (
      <div className="w-full">
        <div className="relative w-full">
          <div className="relative h-[300px] w-full md:h-[420px]">
            <img
              src={work.backdrop}
              alt={work.title}
              title={`${work.title} 剧照`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-primary-950/60 to-primary-950/70" />
          </div>
        </div>

        <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
          <div className="-mt-28 relative flex flex-col gap-6 md:-mt-36 md:flex-row md:gap-8">
            <div className="w-40 shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-900 md:w-52" style={{ aspectRatio: '2 / 3' }}>
              <img
                src={work.cover}
                alt={work.title}
                title={`${work.title} 海报`}
                className="h-full w-full object-cover object-top"
              />
            </div>

            <div className="flex-1 pt-2 md:pt-20">
              <div className="text-foreground-700">
                <Breadcrumb
                  items={[
                    { label: '首页', to: '/' },
                    { label: typeTitle, to: `/category/${work.type}` },
                    { label: work.title },
                  ]}
                />
              </div>
              <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
                {work.title}
                <span className="ml-2 align-middle text-lg font-normal text-foreground-500">（{work.year}）</span>
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1 rounded-md bg-accent-500 px-2.5 py-1 text-sm font-bold text-background-50">
                  <i className="ri-star-fill" />
                  {work.rating.toFixed(1)}
                </span>
                {work.genres.map((g) => (
                  <span key={g} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                    {g}
                  </span>
                ))}
              </div>

              <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">导演</dt>
                  <dd className="text-foreground-900">{work.director}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">地区</dt>
                  <dd className="text-foreground-900">{work.region}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">主演</dt>
                  <dd className="text-foreground-900">{work.cast.join(' / ')}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-500">时长</dt>
                  <dd className="text-foreground-900">{work.duration}</dd>
                </div>
              </dl>
            </div>
          </div>

          <div className="mt-10 border-t border-background-200 pt-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950">剧情简介</h2>
            <p className="mt-4 max-w-4xl text-sm leading-loose text-foreground-800 md:text-base">{work.summary}</p>
            <p className="mt-4 max-w-4xl text-sm leading-loose text-foreground-700">
              作为一部{work.genres.join('、')}题材的{typeTitle}，《{work.title}》以扎实的叙事与细腻的人物刻画获得
              {work.rating.toFixed(1)} 分的评价。作品在{work.region}取景，由{work.director}执导，
              {work.cast.join('、')}等实力演员联袂出演，整体呈现出兼具观赏性与情感厚度的质感。
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={`/category/${work.type}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-left-line" />
              返回{typeTitle}分类
            </Link>
          </div>
        </div>

        {relatedWorks.length > 0 && (
          <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6 md:py-14">
            <div className="mb-6 flex items-center gap-2">
              <span className="h-5 w-1 rounded-full bg-accent-500" />
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">相关推荐</h2>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-6 lg:grid-cols-4">
              {relatedWorks.map((w) => (
                <WorkCard key={w.id} work={w} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  const newsArticle = article!;
  const paragraphs = [
    newsArticle.summary,
    `据本站了解，相关话题自发布以来持续受到关注，不少观众在社交平台分享了各自的观后感。有评论认为，《${
      newsArticle.category
    }》所呈现的创作态度，为同类型作品提供了新的思路。`,
    `更多关于本条资讯的后续进展，枫与铃将持续跟进报道。如果你对影视动态感兴趣，欢迎返回首页浏览最新影视资讯与热门推荐。`,
  ];

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-4xl px-4 py-8 md:px-6 md:py-10">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '影视资讯' }, { label: newsArticle.category }]} />
        <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-3xl">
          {newsArticle.title}
        </h1>
        <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-foreground-500">
          <span className="rounded bg-accent-100 px-2 py-0.5 font-medium text-accent-800">{newsArticle.category}</span>
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line" />
            {newsArticle.date}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-leaf-line" />
            枫与铃编辑部
          </span>
        </div>

        <div className="mt-6 w-full overflow-hidden rounded-lg bg-background-900" style={{ aspectRatio: '16 / 9' }}>
          <img
            src={newsArticle.cover}
            alt={newsArticle.title}
            title={`${newsArticle.title} 配图`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-8 space-y-5 text-sm leading-loose text-foreground-800 md:text-base">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        <div className="mt-10 rounded-lg border border-background-200 bg-background-100 p-5">
          <h3 className="font-heading text-base font-bold text-foreground-950">更多影视资讯</h3>
          <ul className="mt-4 space-y-3">
            {relatedNews.map((n) => (
              <li key={n.id}>
                <Link to={`/detail/${n.id}`} className="group flex items-center gap-2 text-sm text-foreground-800 hover:text-primary-600">
                  <i className="ri-article-line text-accent-500" />
                  <span className="line-clamp-1">{n.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </div>
    </div>
  );
}